import React from 'react';
import { motion } from 'framer-motion';
import Navbar from './Navbar';
import GeometricBackground from './GeometricBackground';

const RoadmapPhase = ({ phase, status, title, items, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="glass-effect p-6 rounded-xl hover:shadow-lg transition-all duration-300 backdrop-blur-lg bg-white/30 border border-white/20 shadow-xl"
  >
    <div className="flex items-center gap-3 mb-4">
      <span className="text-2xl">{status}</span>
      <h3 className="text-xl font-bold text-gray-800">{phase}</h3>
    </div>
    <h4 className="text-lg font-semibold text-gray-700 mb-3">{title}</h4>
    <ul className="space-y-2">
      {items.map((item, index) => (
        <motion.li
          key={index}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: delay + 0.1 * index }}
          className="text-gray-600 flex items-start gap-2"
        >
          <span className="text-[#BFEA15] mt-1">•</span>
          <span>{item}</span>
        </motion.li>
      ))}
    </ul>
  </motion.div>
);

const Roadmap = () => {
  const roadmapData = [
    {
      phase: "Phase 1: Foundation (Q1 2025)",
      status: "✓",
      title: "Core Platform Launch - Completed",
      items: [
        "Real-time Solana portfolio tracking with automatic transaction categorization",
        "Basic tax calculations and reporting functionality",
        "Secure wallet integration with encryption",
        "Web-based dashboard with essential analytics",
        "Initial API endpoints for developer access"
      ]
    },
    {
      phase: "Phase 2: Expansion (Q2 2025)",
      status: "⚡",
      title: "Multi-Chain Evolution - In Progress",
      items: [
        "Cross-chain portfolio management across Solana, Ethereum, and emerging L2s",
        "DeFi protocol integration for automated yield tracking",
        "Smart contract interaction history and gas fee analytics",
        "Advanced cost basis calculations (FIFO, LIFO, HIFO)",
        "Institutional-grade security measures with optional cold storage tracking",
        "Developer API expansion with detailed documentation"
      ]
    },
    {
      phase: "Phase 3: Intelligence (Q3 2025)",
      status: "🎯",
      title: "Advanced Analytics Suite",
      items: [
        "Machine learning-powered portfolio insights and risk analysis",
        "Tax-loss harvesting opportunities with automation options",
        "Custom reporting engine for different jurisdictions",
        "Professional trader features including P&L attribution",
        "Integration with major accounting software",
        "Real-time tax liability forecasting"
      ]
    },
    {
      phase: "Phase 4: Professional Tools (Q4 2025)",
      status: "📱",
      title: "Mobile and Professional Suite",
      items: [
        "Native iOS and Android applications with biometric security",
        "Real-time push notifications for important portfolio events",
        "Professional account management for tax practitioners",
        "Team collaboration features for institutions",
        "White-label solutions for businesses",
        "Automated audit trail generation"
      ]
    },
    {
      phase: "Phase 5: Enterprise (Q1 2026)",
      status: "🏢",
      title: "Enterprise and Institution Focus",
      items: [
        "Multi-signature wallet support for institutional clients",
        "Advanced compliance reporting for regulated entities",
        "Custom API solutions for enterprise integration",
        "Automated tax document generation (1099-B, etc.)",
        "Institution-grade security features",
        "Advanced portfolio analytics for fund managers"
      ]
    },
    {
      phase: "Phase 6: Innovation (Q2 2026)",
      status: "🔮",
      title: "Next-Generation Features",
      items: [
        "Zero-knowledge proof integration for private portfolio sharing",
        "Cross-chain DeFi position management",
        "AI-powered tax optimization strategies",
        "Real-time market correlation analysis",
        "Advanced derivatives and options tracking",
        "Integration with emerging blockchain networks"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="fixed inset-0">
        <GeometricBackground />
      </div>
      
      <div className="relative z-10">
        <Navbar />
        
        <main className="container mx-auto px-4 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl sm:text-5xl font-bold font-space-grotesk bg-gradient-to-r from-[#BFEA15] to-[#D9F273] bg-clip-text text-transparent mb-4">
              Product Roadmap 2025-2026
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our vision is to revolutionize crypto tax reporting and portfolio intelligence through continuous innovation and user-centric development.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {roadmapData.map((phase, index) => (
              <RoadmapPhase key={index} {...phase} delay={0.2 + index * 0.1} />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            className="mt-16 p-8 glass-effect rounded-xl backdrop-blur-lg bg-white/30 border border-white/20 shadow-xl"
          >
            <h3 className="text-xl font-bold text-gray-800 mb-6">Continuous Improvements</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Regular security audits and penetration testing",
                "Performance optimization and scalability improvements",
                "User interface refinements based on feedback",
                "Regulatory compliance updates",
                "New blockchain network support",
                "Enhanced documentation and educational resources",
                "Community-driven feature development"
              ].map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.3 + index * 0.1 }}
                  className="text-gray-600 flex items-start gap-2"
                >
                  <span className="text-[#BFEA15] mt-1">•</span>
                  <span>{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default Roadmap;
